import {
  buildInternalTokenProperties,
  defaultThemeValues,
  WIDGET_SCHEME_ATTRIBUTE,
  WIDGET_TOKEN_SCOPE_CLASS,
  type ResolvedThemeValues,
} from './css';
import { contrastRatio, mixColors, rgbToHex } from './contrast';
import {
  darkPalette,
  lightPalette,
  tokenToCssValue,
  type WidgetPalette,
  type WidgetScheme,
} from './model';

export const PUBLIC_THEME_HOOKS = Object.freeze({
  accent: '--fmp-theme-accent',
  onAccent: '--fmp-theme-on-accent',
  surface: '--fmp-theme-surface',
  surfaceSubtle: '--fmp-theme-surface-subtle',
  text: '--fmp-theme-text',
  textMuted: '--fmp-theme-text-muted',
  border: '--fmp-theme-border',
  focus: '--fmp-theme-focus',
  radius: '--fmp-theme-radius',
  fontFamily: '--fmp-theme-font-family',
} as const);

export type PublicThemeHook = typeof PUBLIC_THEME_HOOKS[keyof typeof PUBLIC_THEME_HOOKS];
export type RawThemeOverrides = Readonly<Record<string, string | undefined>>;

const PUBLIC_HOOK_NAMES = new Set<string>(Object.values(PUBLIC_THEME_HOOKS));
const COLOR_HOOKS = new Set<PublicThemeHook>([
  PUBLIC_THEME_HOOKS.accent,
  PUBLIC_THEME_HOOKS.onAccent,
  PUBLIC_THEME_HOOKS.surface,
  PUBLIC_THEME_HOOKS.surfaceSubtle,
  PUBLIC_THEME_HOOKS.text,
  PUBLIC_THEME_HOOKS.textMuted,
  PUBLIC_THEME_HOOKS.border,
  PUBLIC_THEME_HOOKS.focus,
]);

export interface ThemeTransactionResult {
  readonly accepted: boolean;
  readonly scheme: WidgetScheme;
  readonly values: ResolvedThemeValues;
  readonly internalProperties: Readonly<Record<`--fmp-${string}`, string>>;
  readonly errors: readonly string[];
}

function normalizedHostScheme(
  hostDeclaration: string | null | undefined
): 'light' | 'dark' | 'light dark' | 'dark light' | null {
  const tokens = hostDeclaration?.trim().toLowerCase().split(/\s+/).filter(Boolean) ?? [];
  const onlyCount = tokens.filter((token) => token === 'only').length;
  if (onlyCount > 1) return null;
  const schemes = tokens.filter((token) => token !== 'only');
  const normalized = schemes.join(' ');
  return normalized === 'light' ||
    normalized === 'dark' ||
    normalized === 'light dark' ||
    normalized === 'dark light'
    ? normalized
    : null;
}

function paletteFor(scheme: WidgetScheme): WidgetPalette {
  return scheme === 'dark' ? darkPalette : lightPalette;
}

/** Pure host-declaration/preference truth table from ADR-028 §14A. */
export function resolveColorScheme(
  hostDeclaration: string | null | undefined,
  prefersDark: boolean | null | undefined
): WidgetScheme {
  const declaration = normalizedHostScheme(hostDeclaration);
  if (declaration === 'light') return 'light';
  if (declaration === 'dark') return 'dark';
  if (declaration === 'light dark' || declaration === 'dark light') {
    return prefersDark === true ? 'dark' : 'light';
  }
  return prefersDark === true ? 'dark' : 'light';
}

function hostForcesSingleScheme(hostDeclaration: string | null | undefined): boolean {
  const declaration = normalizedHostScheme(hostDeclaration);
  return declaration === 'light' || declaration === 'dark';
}

function parseOpaqueColor(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(value);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  if (/^#[0-9a-f]{6}$/.test(value)) return value;

  const rgb =
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(value) ??
    /^rgb\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})\s*\)$/.exec(value);
  if (!rgb) return null;
  const channels = rgb.slice(1).map(Number);
  if (channels.some((channel) => channel > 255)) return null;
  return rgbToHex({ r: channels[0], g: channels[1], b: channels[2] });
}

function parseRadius(raw: string): string | null {
  const value = raw.trim().toLowerCase();
  if (value === '0') return value;
  const match = /^(\d+(?:\.\d+)?)px$/.exec(value);
  if (!match) return null;
  const radius = Number(match[1]);
  return Number.isFinite(radius) && radius >= 0 && radius <= 24 ? `${radius}px` : null;
}

const FONT_MEMBER =
  /^(?:inherit|system-ui|sans-serif|serif|monospace|cursive|fantasy|ui-sans-serif|ui-serif|ui-monospace|"[a-z0-9][a-z0-9 ._-]{0,63}"|'[a-z0-9][a-z0-9 ._-]{0,63}'|[a-z][a-z0-9_-]{0,31})$/i;
const DISALLOWED_FONT_FAMILY_KEYWORDS = new Set(['initial', 'unset', 'revert', 'revert-layer']);

function parseFontFamily(raw: string): string | null {
  const value = raw.trim();
  if (value.length === 0 || value.length > 256 || /[;{}\\]/.test(value)) return null;
  if (/\b(?:var|env|url|calc|min|max|clamp)\s*\(/i.test(value)) return null;
  const members = value.split(',').map((member) => member.trim());
  if (members.length > 1 && members.some((member) => member.toLowerCase() === 'inherit')) {
    return null;
  }
  if (
    members.length < 1 ||
    members.length > 5 ||
    members.some(
      (member) =>
        !FONT_MEMBER.test(member) || DISALLOWED_FONT_FAMILY_KEYWORDS.has(member.toLowerCase())
    )
  ) {
    return null;
  }
  return members.join(', ');
}

function minimum(
  errors: string[],
  name: string,
  foreground: string,
  background: string,
  threshold: 3 | 4.5
): void {
  const ratio = contrastRatio(foreground, background);
  if (ratio < threshold) errors.push(`${name} is ${ratio.toFixed(2)}:1; requires ${threshold}:1`);
}

function validateRealPairings(values: ResolvedThemeValues, palette: WidgetPalette): string[] {
  const errors: string[] = [];
  minimum(errors, 'text/surface', values.text, values.surface, 4.5);
  minimum(errors, 'text/subtle-surface', values.text, values.surfaceSubtle, 4.5);
  minimum(errors, 'text/hover-surface', values.text, values.surfaceHover, 4.5);
  minimum(errors, 'secondary-text/surface', values.textSecondary, values.surface, 4.5);
  minimum(errors, 'secondary-text/subtle-surface', values.textSecondary, values.surfaceSubtle, 4.5);
  minimum(errors, 'secondary-text/hover-surface', values.textSecondary, values.surfaceHover, 4.5);
  minimum(errors, 'muted-text/surface', values.textMuted, values.surface, 4.5);
  minimum(errors, 'muted-text/subtle-surface', values.textMuted, values.surfaceSubtle, 4.5);
  minimum(errors, 'muted-text/hover-surface', values.textMuted, values.surfaceHover, 4.5);
  minimum(errors, 'on-accent/accent', values.onAccent, values.accent, 4.5);
  minimum(errors, 'on-accent/accent-hover', values.onAccent, values.accentHover, 4.5);
  minimum(errors, 'accent-text/surface', values.accent, values.surface, 4.5);
  minimum(errors, 'accent/progress-track', values.accent, values.surfaceSubtle, 3);
  minimum(errors, 'focus/surface', values.focus, values.surface, 3);
  minimum(errors, 'focus/subtle-surface', values.focus, values.surfaceSubtle, 3);
  minimum(errors, 'border/surface', values.border, values.surface, 3);
  minimum(errors, 'border/subtle-surface', values.border, values.surfaceSubtle, 3);
  minimum(errors, 'border/hover-surface', values.border, values.surfaceHover, 3);
  minimum(errors, 'text/info-surface', values.text, tokenToCssValue(palette.status.info.surface), 4.5);
  minimum(errors, 'secondary-text/info-surface', values.textSecondary, tokenToCssValue(palette.status.info.surface), 4.5);
  minimum(errors, 'muted-text/info-surface', values.textMuted, tokenToCssValue(palette.status.info.surface), 4.5);
  minimum(errors, 'accent-text/info-surface', values.accent, tokenToCssValue(palette.status.info.surface), 4.5);
  minimum(errors, 'secondary-text/danger-surface', values.textSecondary, tokenToCssValue(palette.status.danger.surface), 4.5);
  minimum(errors, 'muted-text/danger-surface', values.textMuted, tokenToCssValue(palette.status.danger.surface), 4.5);
  for (const status of ['success', 'warning', 'danger', 'info'] as const) {
    minimum(
      errors,
      `${status}-text/surface`,
      tokenToCssValue(palette.status[status].text),
      values.surface,
      4.5
    );
  }
  return errors;
}

/**
 * Parse and validate the entire declared override as one transaction. A single
 * malformed/unsupported/low-contrast member returns the complete safe palette;
 * no raw host value is ever copied to an internal property.
 */
export function resolveThemeTransaction(
  scheme: WidgetScheme,
  overrides: RawThemeOverrides = {}
): ThemeTransactionResult {
  const palette = paletteFor(scheme);
  const safe = defaultThemeValues(palette);
  const parsed = new Map<PublicThemeHook, string>();
  const errors: string[] = [];

  for (const [name, supplied] of Object.entries(overrides)) {
    if (supplied === undefined) continue;
    if (!PUBLIC_HOOK_NAMES.has(name)) {
      errors.push(`${name}: unsupported public theme hook`);
      continue;
    }
    const hook = name as PublicThemeHook;
    let normalized: string | null;
    if (COLOR_HOOKS.has(hook)) normalized = parseOpaqueColor(supplied);
    else if (hook === PUBLIC_THEME_HOOKS.radius) normalized = parseRadius(supplied);
    else normalized = parseFontFamily(supplied);
    if (normalized === null) errors.push(`${name}: malformed or unsupported value`);
    else parsed.set(hook, normalized);
  }

  if (errors.length > 0) {
    return Object.freeze({
      accepted: false,
      scheme,
      values: safe,
      internalProperties: buildInternalTokenProperties(palette, safe),
      errors: Object.freeze(errors),
    });
  }

  const accent = parsed.get(PUBLIC_THEME_HOOKS.accent) ?? safe.accent;
  const surface = parsed.get(PUBLIC_THEME_HOOKS.surface) ?? safe.surface;
  const surfaceSubtle = parsed.get(PUBLIC_THEME_HOOKS.surfaceSubtle) ?? safe.surfaceSubtle;
  const text = parsed.get(PUBLIC_THEME_HOOKS.text) ?? safe.text;
  const values: ResolvedThemeValues = Object.freeze({
    accent,
    accentHover: parsed.has(PUBLIC_THEME_HOOKS.accent) ? accent : safe.accentHover,
    accentSubtle: parsed.has(PUBLIC_THEME_HOOKS.accent)
      ? mixColors(accent, surface, 0.12)
      : safe.accentSubtle,
    onAccent: parsed.get(PUBLIC_THEME_HOOKS.onAccent) ?? safe.onAccent,
    surface,
    surfaceSubtle,
    surfaceHover: parsed.has(PUBLIC_THEME_HOOKS.accent) || parsed.has(PUBLIC_THEME_HOOKS.surface)
      ? mixColors(accent, surface, 0.08)
      : safe.surfaceHover,
    text,
    textSecondary: parsed.has(PUBLIC_THEME_HOOKS.text) ? text : safe.textSecondary,
    textMuted: parsed.get(PUBLIC_THEME_HOOKS.textMuted) ?? safe.textMuted,
    border: parsed.get(PUBLIC_THEME_HOOKS.border) ?? safe.border,
    focus: parsed.get(PUBLIC_THEME_HOOKS.focus) ?? safe.focus,
    radius: parsed.get(PUBLIC_THEME_HOOKS.radius) ?? safe.radius,
    fontFamily: parsed.get(PUBLIC_THEME_HOOKS.fontFamily) ?? safe.fontFamily,
  });
  const contrastErrors = validateRealPairings(values, palette);
  minimum(
    contrastErrors,
    'on-accent/danger-action',
    values.onAccent,
    tokenToCssValue(palette.status.danger.text),
    4.5
  );
  if (contrastErrors.length > 0) {
    return Object.freeze({
      accepted: false,
      scheme,
      values: safe,
      internalProperties: buildInternalTokenProperties(palette, safe),
      errors: Object.freeze(contrastErrors),
    });
  }

  return Object.freeze({
    accepted: true,
    scheme,
    values,
    internalProperties: buildInternalTokenProperties(palette, values),
    errors: Object.freeze([]),
  });
}

export interface ComputedStyleReader {
  readonly colorScheme?: string;
  getPropertyValue(property: string): string;
}

function authoredThemeValues(scope: HTMLElement): ReadonlyMap<PublicThemeHook, readonly string[]> {
  const values = new Map<PublicThemeHook, string[]>();
  const elements: HTMLElement[] = [];
  for (let element: HTMLElement | null = scope; element; element = element.parentElement) {
    elements.push(element);
  }

  const record = (
    style: Pick<CSSStyleDeclaration, 'getPropertyValue' | 'length'> & {
      readonly [index: number]: string | undefined;
      item?: (index: number) => string;
    }
  ): void => {
    const declared = new Set<string>();
    for (let index = 0; index < style.length; index += 1) {
      const property = typeof style.item === 'function' ? style.item(index) : style[index];
      if (property) declared.add(property);
    }
    for (const hook of Object.values(PUBLIC_THEME_HOOKS)) {
      const value = style.getPropertyValue(hook).trim();
      if (value === '' && !declared.has(hook)) continue;
      const existing = values.get(hook) ?? [];
      existing.push(value);
      values.set(hook, existing);
    }
  };

  for (const element of elements) record(element.style);

  const visitRules = (rules: CSSRuleList): void => {
    for (const rule of Array.from(rules)) {
      if ('selectorText' in rule && 'style' in rule) {
        const selectorText = String(rule.selectorText);
        let matches = false;
        try {
          matches = elements.some((element) => element.matches(selectorText));
        } catch {
          // Invalid or unsupported selectors cannot be the source of a
          // computed declaration in this browser.
        }
        if (matches) record(rule.style as CSSStyleDeclaration);
      }

      if ('cssRules' in rule) {
        try {
          visitRules(rule.cssRules as CSSRuleList);
        } catch {
          // A grouping rule may be unavailable in a partial DOM. The final
          // computed-without-authored check below still fails closed.
        }
      }
    }
  };

  for (const sheet of Array.from(scope.ownerDocument.styleSheets)) {
    try {
      visitRules(sheet.cssRules);
    } catch {
      // Cross-origin CSSOM is intentionally opaque. A computed public hook
      // with no inspectable authored declaration is rejected below.
    }
  }

  return values;
}

const UNINSPECTABLE_THEME_VALUE = '__fmp_uninspectable_authored_value__';

function isSupportedAuthoredHookValue(hook: PublicThemeHook, value: string): boolean {
  if (COLOR_HOOKS.has(hook)) return parseOpaqueColor(value) !== null;
  if (hook === PUBLIC_THEME_HOOKS.radius) return parseRadius(value) !== null;
  return parseFontFamily(value) !== null;
}

/**
 * Acquire the ten public hooks. Passing an element preserves authored syntax
 * so computed-value substitution cannot turn a forbidden var()/env()/calc()
 * expression into an apparently safe literal. Programmatic callers that
 * already hold raw values pass them directly to resolveThemeTransaction.
 */
export function readPublicThemeOverrides(
  scope: HTMLElement,
  computedStyle?: ComputedStyleReader
): RawThemeOverrides {
  const style = computedStyle
    ?? (typeof getComputedStyle === 'function' ? getComputedStyle(scope) : undefined);
  if (!style) return Object.freeze({});
  const authored = authoredThemeValues(scope);
  const overrides: Record<string, string> = {};
  for (const hook of Object.values(PUBLIC_THEME_HOOKS)) {
    const declarations = authored.get(hook) ?? [];
    const unsupported = declarations.find(
      (declaration) => !isSupportedAuthoredHookValue(hook, declaration)
    );
    if (unsupported !== undefined) {
      overrides[hook] = unsupported;
      continue;
    }
    const value = style.getPropertyValue(hook).trim();
    if (value === '') {
      if (declarations.length > 0) overrides[hook] = UNINSPECTABLE_THEME_VALUE;
      continue;
    }
    overrides[hook] = declarations.length === 0 ? UNINSPECTABLE_THEME_VALUE : value;
  }
  return Object.freeze(overrides);
}

export function applyThemeTransaction(
  scope: HTMLElement,
  scheme: WidgetScheme,
  overrides: RawThemeOverrides = {}
): ThemeTransactionResult {
  const result = resolveThemeTransaction(scheme, overrides);
  for (const [property, value] of Object.entries(result.internalProperties)) {
    scope.style.setProperty(property, value);
  }
  scope.setAttribute(WIDGET_SCHEME_ATTRIBUTE, scheme);
  return result;
}

export interface InstallWidgetTokenScopeOptions {
  readonly hostColorScheme?: string | null;
  readonly prefersDark?: boolean | null;
  readonly theme?: RawThemeOverrides;
  readonly readComputedStyle?: (scope: HTMLElement) => ComputedStyleReader;
}

export interface InstalledWidgetTokenScope {
  readonly hostColorScheme: string | null;
  readonly scheme: WidgetScheme;
  readonly overrides: RawThemeOverrides;
  readonly theme: ThemeTransactionResult;
}

const HOST_COLOR_SCHEME_STATE = Symbol.for('find-my-part.widget.host-color-scheme');
type HostSchemeCarrier = HTMLElement & { [HOST_COLOR_SCHEME_STATE]?: string | null };

function rememberHostColorScheme(scope: HTMLElement, value: string | null): void {
  Object.defineProperty(scope, HOST_COLOR_SCHEME_STATE, {
    configurable: true,
    enumerable: false,
    writable: true,
    value,
  });
}

/** Install defaults, resolve scheme, and validate hooks synchronously before React renders. */
export function installWidgetTokenScope(
  scope: HTMLElement,
  options: InstallWidgetTokenScopeOptions = {}
): InstalledWidgetTokenScope {
  const reader = options.readComputedStyle?.(scope)
    ?? (typeof getComputedStyle === 'function' ? getComputedStyle(scope) : undefined);
  const carrier = scope as HostSchemeCarrier;
  const hasExplicitHostScheme = Object.prototype.hasOwnProperty.call(options, 'hostColorScheme');
  const hasRememberedHostScheme = Object.prototype.hasOwnProperty.call(
    carrier,
    HOST_COLOR_SCHEME_STATE
  );
  const hostColorScheme = hasExplicitHostScheme
    ? options.hostColorScheme ?? null
    : hasRememberedHostScheme
      ? carrier[HOST_COLOR_SCHEME_STATE] ?? null
      : reader?.colorScheme ?? null;
  if (hasExplicitHostScheme || !hasRememberedHostScheme) {
    rememberHostColorScheme(scope, hostColorScheme);
  }
  const prefersDark = options.prefersDark ?? (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : null
  );
  const scheme = resolveColorScheme(hostColorScheme, prefersDark);
  scope.classList.add(WIDGET_TOKEN_SCOPE_CLASS);
  const themeReader = options.readComputedStyle?.(scope)
    ?? (typeof getComputedStyle === 'function' ? getComputedStyle(scope) : undefined);
  const overrides = options.theme ?? (themeReader ? readPublicThemeOverrides(scope, themeReader) : {});
  return Object.freeze({
    hostColorScheme,
    scheme,
    overrides,
    theme: applyThemeTransaction(scope, scheme, overrides),
  });
}

export interface SchemeMediaQueryList {
  readonly matches: boolean;
  addEventListener?(type: 'change', listener: (event: { readonly matches: boolean }) => void): void;
  removeEventListener?(type: 'change', listener: (event: { readonly matches: boolean }) => void): void;
  addListener?(listener: (event: { readonly matches: boolean }) => void): void;
  removeListener?(listener: (event: { readonly matches: boolean }) => void): void;
}

export type SchemeMatchMedia = (query: string) => SchemeMediaQueryList;

/** Subscribe only when the host has not forced one scheme; always returns cleanup. */
export function subscribeToSchemePreference(
  hostDeclaration: string | null | undefined,
  onSchemeChange: (scheme: WidgetScheme) => void,
  matchMedia: SchemeMatchMedia | undefined = typeof window !== 'undefined'
    ? window.matchMedia.bind(window)
    : undefined
): () => void {
  if (hostForcesSingleScheme(hostDeclaration) || !matchMedia) return () => undefined;
  const query = matchMedia('(prefers-color-scheme: dark)');
  const listener = (event: { readonly matches: boolean }): void => {
    onSchemeChange(resolveColorScheme(hostDeclaration, event.matches));
  };
  if (query.addEventListener) query.addEventListener('change', listener);
  else query.addListener?.(listener);
  return () => {
    if (query.removeEventListener) query.removeEventListener('change', listener);
    else query.removeListener?.(listener);
  };
}
